import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useStore } from 'vuex'
import type { Account } from '@/types'
import { usePersonalDetailsDisplay } from '@/composables/usePersonalDetailsDisplay'
import { useSwiftaidAuthorisationStatus } from '@/composables/useSwiftaidAuthorisationStatus'

export function useMatchingSignals() {
  const { t } = useI18n()
  const store = useStore()
  const { identitySummary } = usePersonalDetailsDisplay()
  const { isAuthorised, isStatusKnown } = useSwiftaidAuthorisationStatus()

  const primaryEmail = computed(
    () => store.state.donor?.userDetails?.emailAddress as string | undefined
  )

  const additionalEmails = computed(() => {
    const accounts = store.state.accounts?.emailAccounts ?? []
    return accounts.filter((account: { isPrimary?: boolean; id: string }) => {
      if (account.isPrimary) return false
      if (primaryEmail.value && account.id === primaryEmail.value) return false
      return true
    })
  })

  const pendingEmails = computed(
    () =>
      (store.state.accounts?.pendingEmailVerifications ?? []) as Array<{
        email: string
      }>
  )

  const emailCount = computed(() => {
    let count = primaryEmail.value ? 1 : 0
    count += additionalEmails.value.length
    count += pendingEmails.value.length
    return count
  })

  const matchingCards = computed(() => {
    const fidel = (store.state.accounts.fidelAccounts ?? []) as Account[]
    const cards = (store.state.accounts.cardAccounts ?? []) as Account[]
    const par = (store.state.accounts.parAccounts ?? []) as Account[]
    return [...fidel, ...cards, ...par]
  })

  const cardCount = computed(() => matchingCards.value.length)

  const emailsSummary = computed(() => {
    if (emailCount.value === 0) {
      return t('Dashboard.Status.Matching.SummaryEmailsEmpty')
    }
    if (emailCount.value === 1) {
      return t('Dashboard.Status.Matching.SummaryEmailsOne')
    }
    return t('Dashboard.Status.Matching.SummaryEmailsOther', { count: emailCount.value })
  })

  const cardsSummary = computed(() => {
    if (cardCount.value === 0) {
      return t('Dashboard.Status.Matching.SummaryCardsEmpty')
    }
    if (cardCount.value === 1) {
      return t('Dashboard.Status.Matching.SummaryCardsOne')
    }
    return t('Dashboard.Status.Matching.SummaryCardsOther', { count: cardCount.value })
  })

  const resolvedIdentitySummary = computed(() => {
    if (identitySummary.value) {
      return identitySummary.value
    }
    return t('Dashboard.Status.Matching.SummaryIdentityEmpty')
  })

  return {
    isAuthorised,
    isStatusKnown,
    primaryEmail,
    additionalEmails,
    pendingEmails,
    emailCount,
    matchingCards,
    cardCount,
    resolvedIdentitySummary,
    emailsSummary,
    cardsSummary,
  }
}
